import { useState, type MouseEvent, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Dropdown, Row, Tooltip, Button, Switch, Divider } from 'antd';
import { CheckOutlined, DownOutlined, ExportOutlined } from '@ant-design/icons';
import type { JapanSimDataMeta, SimDataMeta } from '../../../types/domain';
import { useCurrentSimDataId } from '../../../hooks/SimViewer/useCurrentSimDataId';
import { useMetaData } from '../../../hooks/data/useMetaData';
import {
  getPeriod,
  getPrefectureCode,
  isPrefectureCode,
} from '../../../utils/SimViewer/simdata';

type MenuItem = {
  key: string;
  label: ReactNode;
};

const DropdownItem = ({
  simDataMeta,
  isCurrent = false,
}: {
  simDataMeta: SimDataMeta | JapanSimDataMeta;
  isCurrent: boolean;
}): ReactNode => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { t } = useTranslation();

  return (
    <Row
      align="middle"
      key={simDataMeta.simdata_id}
      style={{ height: '42px', alignItems: 'center' }}
    >
      <Row
        onClick={
          isCurrent
            ? undefined
            : (): void => {
                navigate(`/sim-viewer/${simDataMeta.simdata_id}${search}`);
              }
        }
        style={{
          flex: 1,
          alignItems: 'center',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          maxWidth: 800,
          height: '100%',
          opacity: isCurrent ? 0.5 : 1,
          cursor: isCurrent ? 'not-allowed' : 'pointer',
        }}
      >
        {isCurrent && <CheckOutlined style={{ marginRight: 8 }} />}
        {'prefectureCode' in simDataMeta
          ? `${
              isPrefectureCode(simDataMeta.prefectureCode)
                ? t(`prefNames.${simDataMeta.prefectureCode}`)
                : ''
            } - ${t('yearAndMonth', {
              year: simDataMeta.year,
              month: simDataMeta.month,
            })} (${t(simDataMeta.dayType)})`
          : simDataMeta.name}
      </Row>
      <Divider
        type="vertical"
        style={{ height: 20, marginInline: '16px 12px' }}
      />
      <Tooltip placement="top" title={t('openInNewTab')}>
        <Button
          href={`/sim-viewer/${simDataMeta.simdata_id}${search}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            boxSizing: 'border-box',
            padding: 0,
            aspectRatio: '1 / 1',
            borderRadius: '8px',
          }}
        >
          <ExportOutlined />
        </Button>
      </Tooltip>
    </Row>
  );
};

const SimDataSwitcher = (): ReactNode => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { customDataMetas, japanDataMetas } = useMetaData();

  const [showsCustomData, setShowsCustomData] = useState<boolean>(
    (): boolean => {
      return !currentSimDataId || !japanDataMetas.has(currentSimDataId);
    }
  );

  const navigate = useNavigate();
  const { t } = useTranslation();

  const currentSimData = currentSimDataId
    ? customDataMetas.get(currentSimDataId)
    : undefined;
  const currentPeriod = currentSimDataId ? getPeriod(currentSimDataId) : null;

  return (
    <>
      {showsCustomData ? (
        <Dropdown
          menu={{
            items: Array.from(customDataMetas.values()).map(
              (simDataMeta: SimDataMeta): MenuItem => {
                return {
                  key: simDataMeta.simdata_id,
                  label: (
                    <DropdownItem
                      simDataMeta={simDataMeta}
                      isCurrent={simDataMeta.simdata_id === currentSimDataId}
                    />
                  ),
                };
              }
            ),
          }}
          dropdownRender={(menu: ReactNode): ReactNode => (
            <DropdownMenuWrapper>{menu}</DropdownMenuWrapper>
          )}
          trigger={['click']}
          overlayStyle={{
            top: '98px',
            maxHeight: 'calc(100vh - 98px)',
            overflowY: 'auto',
          }}
        >
          <Button
            type="text"
            style={{
              minWidth: 0,
              display: 'inline-flex',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {currentSimData?.name || ''}
            </span>
            <DownOutlined />
          </Button>
        </Dropdown>
      ) : (
        <>
          <Dropdown
            menu={{
              items: Array.from(japanDataMetas.values())
                .filter((simDataMeta: JapanSimDataMeta): boolean => {
                  return (
                    simDataMeta.prefectureCode ===
                    getPrefectureCode(currentSimDataId || '')
                  );
                })
                .map((simDataMeta: JapanSimDataMeta): MenuItem => {
                  return {
                    key: simDataMeta.simdata_id,
                    label: (
                      <DropdownItem
                        simDataMeta={simDataMeta}
                        isCurrent={simDataMeta.simdata_id === currentSimDataId}
                      />
                    ),
                  };
                }),
            }}
            dropdownRender={(menu): ReactNode => (
              <DropdownMenuWrapper>{menu}</DropdownMenuWrapper>
            )}
            overlayStyle={{
              top: '98px',
              maxHeight: 'calc(100vh - 98px)',
              overflowY: 'auto',
            }}
            trigger={['click']}
          >
            <Button type="text">
              {currentPeriod
                ? `${
                    isPrefectureCode(currentPeriod.prefectureCode)
                      ? t(`prefNames.${currentPeriod.prefectureCode}`)
                      : ''
                  } - ${t('yearAndMonth', {
                    year: currentPeriod.year,
                    month: currentPeriod.month,
                  })} (${t(currentPeriod.dayType)})`
                : ''}
              <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            type="text"
            href="/"
            onClick={(event: MouseEvent<HTMLAnchorElement>): void => {
              // アクセシビリティのためにhrefを設定しつつ、React Routerを使ってクライアントサイドでページ遷移させる
              event.preventDefault();
              navigate('/');
            }}
          >
            {t('analyzeOtherData')}
          </Button>
        </>
      )}
      {customDataMetas.size > 0 && japanDataMetas.size > 0 && (
        <>
          <Switch
            size="small"
            checked={showsCustomData}
            onChange={(): void => {
              setShowsCustomData(!showsCustomData);
            }}
          />
          <span style={{ marginLeft: 8, verticalAlign: 'middle' }}>
            {t('showCustomData')}
          </span>
        </>
      )}
    </>
  );
};

const DropdownMenuWrapper = styled.div`
  .ant-dropdown-menu-item {
    padding-block: 0;
  }
`;

export { SimDataSwitcher };
